import React from "react";

import { useReactifySearchContext } from "../hooks";
import { debug } from "../utility";
import { ConfigRedirect, ElasticProduct } from "../types";

export const useSearch = (): {
  /** The current search term */
  searchTerm: string;
  /** Function for changing the current search term */
  setSearchTerm: (searchTerm?: string) => void;
  /** Function for navigating to the search page, includes logic for redirects */
  submitSearchTerm: (searchTerm?: string) => void;
  /** Function for retrieving search results for an opensearch query */
  submitSearchQuery: (searchBody: any, searchHeaders: Record<string, string>) => Promise<Array<ElasticProduct>>;
  /** Used to determine if instant search results should be displayed */
  showInstantSearchResults: boolean;
  /** Used to manually set the "showInstantSearchResults" value */
  setShowInstantSearchResults: React.Dispatch<React.SetStateAction<boolean>>;
  /** Contains any matching redirect for the current search term */
  searchRedirect: ConfigRedirect | undefined;
} => {
  const { options, config, search } = useReactifySearchContext();

  const handleSubmitSearchQuery = React.useCallback(
    async (searchBody: any, searchHeaders: Record<string, string>) => {
      debug.log("useSearch", "submitSearchQuery[body]", searchBody);
      debug.log("useSearch", "submitSearchQuery[headers]", searchHeaders);
      
      const response = await fetch(`${config.endpoint}/${config.index}/_search`, {
        method: "POST",
        body: JSON.stringify(searchBody),
        headers: searchHeaders,
      }).then((response) => {
        debug.log("useSearch", "submitSearchQuery[status]", response.status);
        
        return response.json();
      }).catch((error) => {
        debug.log("useSearch", "submitSearchQuery[error]", error);

        return error;
      })

      debug.log("useSearch", "submitSearchQuery[response]", response);

      if (response?.hits?.hits?.length > 0) {
        return response.hits.hits.map((item: { _source: ElasticProduct; }) => item._source);
      }

      return [];
    },
    [config.endpoint, config.index]
  );

  const handleSubmitSearchTerm = React.useCallback(
    (searchTerm: string = search.searchTerm) => {
      debug.log("useSearch", "submitSearchTerm[query]", searchTerm);

      if (!searchTerm) {
        return;
      }

      const redirect = config.redirects.find(
        (redirect) =>
          redirect.query.trim().toLowerCase() ===
          searchTerm.trim().toLowerCase()
      );

      if (redirect) {
        debug.log("useSearch", "submitSearchTerm[redirect]", redirect);
      }

      search.setShowInstantSearchResults(false);

      if (options.onRedirect) {
        options.onRedirect(
          redirect ? "redirect" : "search",
          redirect
            ? redirect.url
            : `/search?q=${encodeURIComponent(searchTerm)}`
        );
      } else if (redirect) {
        window.location.href = redirect.url;
      } else {
        window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
      }
    },
    [search.searchTerm]
  );

  const handleSetSearchTerm = React.useCallback(
    (searchTerm: string = "") => {
      debug.log("useSearch", "setSearchTerm[searchTerm]", searchTerm);

      search.setSearchTerm(searchTerm);
    },
    [search.searchTerm]
  );

  return {
    searchTerm: search.searchTerm,
    setSearchTerm: handleSetSearchTerm,
    submitSearchTerm: handleSubmitSearchTerm,
    submitSearchQuery: handleSubmitSearchQuery,
    showInstantSearchResults: !!search.showInstantSearchResults && !!search.searchTerm,
    setShowInstantSearchResults: search.setShowInstantSearchResults,
    searchRedirect: search.searchRedirect,
  };
};
